import { Component, Inject, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { TokeetImage } from '@tv3/models/rental/tokeet-image'
import {
  ActionFailed,
  EnhanceImageResponse,
  loadRentalImagesComplete,
  RentalService,
  selectCurrentAccount,
  selectOnce,
} from '@tokeet-frontend/tv3-platform'
import { Store } from '@ngrx/store'
import { switchMap } from 'rxjs/operators'
import { Lightbox } from 'ngx-lightbox'

export type RentalImageEnhanceApplyDialogData = {
  rentalId: string
  image: TokeetImage
}

@Component({
  selector: 'app-rental-image-enhance-apply-dialog',
  templateUrl: './rental-image-enhance-apply-dialog.component.html',
  styleUrls: ['./rental-image-enhance-apply-dialog.component.scss'],
  host: { class: 'modal-content' },
})
export class RentalImageEnhanceApplyDialogComponent implements OnInit {
  isLoading = true
  isApplying = false
  error: string = null
  enhancedBase64: string = null
  enhancedFormat: string = null

  constructor(
    public dialogRef: MatDialogRef<RentalImageEnhanceApplyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RentalImageEnhanceApplyDialogData,
    private rentalService: RentalService,
    private store: Store<any>,
    private lightbox: Lightbox
  ) {}

  ngOnInit() {
    this.enhance()
  }

  enhance() {
    this.isLoading = true
    this.error = null
    this.enhancedBase64 = null
    this.enhancedFormat = null

    this.store
      .pipe(
        selectOnce(selectCurrentAccount),
        switchMap((account) => this.rentalService.enhanceImage(this.data.image.secureUrl, account.id))
      )
      .subscribe({
        next: (res: EnhanceImageResponse) => {
          this.enhancedBase64 = `data:image/${res.format};base64,${res.base64}`
          this.enhancedFormat = res.format
          this.isLoading = false
        },
        error: (error) => {
          this.error = $localize`Failed to enhance image. Please try again.`
          this.store.dispatch(ActionFailed({ error }))
          this.isLoading = false
        },
      })
  }

  close() {
    this.dialogRef.close()
  }

  apply() {
    if (!this.enhancedBase64) return

    this.isApplying = true
    this.error = null
    const original = this.data.image

    this.rentalService
      .uploadBase64ToCloudinary(this.enhancedBase64)
      .pipe(
        switchMap((cloudinary) =>
          this.rentalService.editImage(original.id, {
            url: cloudinary.url,
            secure_url: cloudinary.secure_url,
            public_id: cloudinary.public_id,
            type: cloudinary.format,
            size: cloudinary.bytes,
            height: cloudinary.height,
            width: cloudinary.width,
            metadata: {
              ...original.metadata,
              enhanced: 1,
              original: {
                url: original.url,
                secure_url: original.secureUrl,
                public_id: original.publicId,
                type: original.type,
                size: original.size,
                height: original.height,
                width: original.width,
              },
            },
          })
        ),
        switchMap(() => this.rentalService.images(this.data.rentalId))
      )
      .subscribe({
        next: (images) => {
          this.store.dispatch(loadRentalImagesComplete({ update: { id: this.data.rentalId, changes: { images } } }))
          this.dialogRef.close()
        },
        error: (error) => {
          this.error = $localize`Failed to apply enhancement. Please try again.`
          this.store.dispatch(ActionFailed({ error }))
          this.isApplying = false
        },
      })
  }

  retry() {
    if (this.isLoading || this.isApplying) return

    if (this.enhancedBase64) {
      this.apply()
      return
    }

    this.enhance()
  }

  openOriginalLightbox() {
    this.lightbox.open([{ src: this.data.image.secureUrl, thumb: this.data.image.secureUrl, caption: '' }], 0)
  }

  openEnhancedLightbox() {
    if (!this.enhancedBase64) return
    this.lightbox.open([{ src: this.enhancedBase64, thumb: this.enhancedBase64, caption: '' }], 0)
  }
}
